import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, Printer, ArrowLeft, FileText } from 'lucide-react';
import Button from '../../components/Button';
import { formatDate, renderCell, coalesce, renderCellDate } from '../../utils/helpers';
import { EQUIPMENT_TYPE_LABELS } from '../../utils/constants';
import { downloadSimpleReportPDF } from '../../utils/pdfGeneratorSimple';

const ReportPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (location.state) {
      setReportData(location.state);
      setLoading(false);
    } else {
      navigate('/reports');
    }
  }, [location, navigate]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    if (reportData) {
      downloadSimpleReportPDF(
        reportData.data,
        reportData.reportType,
        { name: reportData.clientName, company: reportData.clientCompany },
        reportData.generatedAt
      );
    }
  };

  if (loading || !reportData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du rapport...</p>
        </div>
      </div>
    );
  }

  const reportTypeLabels = {
    full: 'Rapport Complet',
    equipment: 'Liste des Équipements',
    maintenance: 'Historique des Maintenances',
    expiring: 'Équipements Expirant Bientôt',
  };

  const maintenanceTypeLabel = (type) => {
    if (type === 'routine') return 'Routine';
    if (type === 'inspection') return 'Inspection';
    if (type === 'repair') return 'Réparation';
    return type;
  };

  const maintenanceStatusLabel = (status) => {
    if (status === 'pending') return 'En attente';
    if (status === 'completed') return 'Terminé';
    if (status === 'cancelled') return 'Annulé';
    if (status === 'scheduled') return 'Planifiée';
    if (status === 'in_progress') return 'En cours';
    return status;
  };

  const equipmentList = reportData.data.equipment || [];
  const maintenanceList = reportData.data.maintenance || [];
  const expiringList = reportData.data.expiring || [];
  const isFullReport = reportData.reportType === 'full';
  const showEquipment = isFullReport || reportData.reportType === 'equipment';
  const showMaintenance = isFullReport || reportData.reportType === 'maintenance';
  const showExpiring = isFullReport || reportData.reportType === 'expiring';
  const generatedAt = reportData.generatedAt ? new Date(reportData.generatedAt) : new Date();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Barre d'actions (ne s'imprime pas) */}
      <div className="print:hidden bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              icon={ArrowLeft}
              onClick={() => navigate('/reports')}
            >
              Retour aux rapports
            </Button>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                icon={Printer}
                onClick={handlePrint}
              >
                Imprimer
              </Button>
              <Button
                variant="primary"
                icon={Download}
                onClick={handleDownload}
              >
                Télécharger PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu du rapport (imprimable) */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white shadow-lg rounded-lg overflow-hidden print:shadow-none print:rounded-none"
        >
          {/* En-tête du rapport */}
          <div className="bg-gradient-to-r from-red-50 to-white border-b-4 border-red-600 p-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <FileText className="w-8 h-8 text-red-600" />
                  <h1 className="text-3xl font-bold text-gray-900">
                    PFE
                  </h1>
                </div>
                <p className="text-gray-600">Gestion des Équipements de Sécurité Incendie</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Date de génération</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDate(generatedAt, 'dd/MM/yyyy HH:mm')}
                </p>
              </div>
            </div>
          </div>

          {/* Informations du rapport */}
          <div className="p-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {reportTypeLabels[reportData.reportType] || 'Rapport'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Client</p>
                <p className="text-lg font-semibold text-gray-900">{renderCell(reportData.clientName)}</p>
              </div>
              {reportData.clientCompany && (
                <div>
                  <p className="text-sm text-gray-500">Société</p>
                  <p className="text-lg font-semibold text-gray-900">{reportData.clientCompany}</p>
                </div>
              )}
            </div>
          </div>

          {/* Section Équipements */}
          {showEquipment && equipmentList.length > 0 && (
            <div className="p-8 border-b border-gray-200">
              <h3 className="text-xl font-bold text-red-600 mb-4">Équipements</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        N° Série
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type Extincteur
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Capacité
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Emplacement
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {equipmentList.map((equipment, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {renderCell(equipment.serial_number)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {renderCell(coalesce(EQUIPMENT_TYPE_LABELS[equipment.type], equipment.type))}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {renderCell(equipment.extinguisher_type)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {renderCell(equipment.capacity)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {renderCell(equipment.location)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              equipment.status === 'operational'
                                ? 'bg-green-100 text-green-800'
                                : equipment.status === 'expired'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {equipment.status === 'operational'
                              ? 'Terminé'
                              : equipment.status === 'expired'
                              ? 'Expiré'
                              : equipment.status === 'needs_maintenance'
                              ? 'Maintenance requise'
                              : equipment.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Total: <span className="font-semibold">{equipmentList.length}</span> équipement(s)
              </p>
            </div>
          )}

          {showEquipment && equipmentList.length === 0 && (
            <div className="p-8 border-b border-gray-200">
              <h3 className="text-xl font-bold text-red-600 mb-2">Équipements</h3>
              <p className="text-sm text-gray-500">Aucun équipement enregistré pour cette période.</p>
            </div>
          )}

          {/* Section Maintenances */}
          {showMaintenance && maintenanceList.length > 0 && (
            <div className="p-8 border-b border-gray-200">
              <h3 className="text-xl font-bold text-red-600 mb-4">Maintenances</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        N° Série
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date prévue
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date réalisée
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Technicien
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {maintenanceList.map((maintenance, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {renderCell(maintenance.serial_number)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {renderCell(maintenanceTypeLabel(maintenance.type))}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {renderCellDate(maintenance.scheduled_date)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {renderCellDate(maintenance.completed_date)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              maintenance.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : maintenance.status === 'cancelled'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {maintenance.status
                              ? maintenanceStatusLabel(maintenance.status)
                              : renderCell(null)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {renderCell(maintenance.technician_name)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Total: <span className="font-semibold">{maintenanceList.length}</span> maintenance(s)
              </p>
            </div>
          )}

          {showMaintenance && maintenanceList.length === 0 && (
            <div className="p-8 border-b border-gray-200">
              <h3 className="text-xl font-bold text-red-600 mb-2">Maintenances</h3>
              <p className="text-sm text-gray-500">Aucune maintenance enregistrée pour cette période.</p>
            </div>
          )}

          {/* Section Équipements expirant */}
          {showExpiring && expiringList.length > 0 && (
            <div className="p-8 border-b border-gray-200">
              <h3 className="text-xl font-bold text-red-600 mb-4">Équipements Expirant Bientôt</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        N° Série
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Emplacement
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date expiration
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Prochaine maintenance
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {expiringList.map((equipment, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {renderCell(equipment.serial_number)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {renderCell(coalesce(EQUIPMENT_TYPE_LABELS[equipment.type], equipment.type))}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {renderCell(equipment.location)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {renderCellDate(equipment.expiration_date)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {renderCellDate(equipment.next_maintenance_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Total: <span className="font-semibold">{expiringList.length}</span> équipement(s) expirant bientôt
              </p>
            </div>
          )}

          {showExpiring && expiringList.length === 0 && (
            <div className="p-8 border-b border-gray-200">
              <h3 className="text-xl font-bold text-red-600 mb-2">Équipements Expirant Bientôt</h3>
              <p className="text-sm text-gray-500">Aucun équipement expirant dans les 10 prochains jours.</p>
            </div>
          )}

          {/* Pied de page */}
          <div className="p-8 bg-gray-50 text-center">
            <p className="text-sm text-gray-600">
              © 2025 PETROFEU ÉQUIPEMENTS - Tous droits réservés
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ReportPreview;

